import { Dispatch, SetStateAction, useState } from 'react';
import { useMount } from './useMount';

/**
 * Este hook es una simplificación de la dinámica de suscripción con cambio de
 * estado ante eventos.
 * 
 * @example
 * 
 * useIsAfter = (id: string) => {
    return useSubscription({
      getInitialState: () => {
        return { isAfter: this.#isAfter(id), isBefore: this.#isBefore(id) };
      },
      makeSubscription: (setState) => {
        return this.#emitter.on('hover', () => {
          setState({
            isAfter: this.#isAfter(id),
            isBefore: this.#isBefore(id),
          });
        });
      },
    });
  };
 */
export function useSubscription<T>({
  getInitialState,
  makeSubscription,
}: {
  getInitialState?: (() => T) | (() => () => T);
  makeSubscription: (setState: Dispatch<SetStateAction<T>>) => () => void;
}) {
  const [state, setState] = useState<T>(getInitialState?.() as T);

  useMount(() => {
    let newState = getInitialState?.();
    if (typeof newState === 'function') newState = (newState as () => T)();

    if (newState !== undefined) setState(newState);

    return makeSubscription(setState);
  });

  return state;
}
